/**
 * The Admin area (Phase 7b, container): its section links and the section's
 * page. Users, groups, sites, device secrets and the audit need `tenant.admin`
 * (tenant-wide); usage needs `billing.view`. The tenant admin surface is the
 * cloud's: on a node's page (which serves no `/me` and none of these routes)
 * the area says so instead of failing section by section.
 */
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useGrants, useServices } from '../../app/hooks.js';
import { AdminNav, type AdminSection } from '../../components/admin/AdminNav.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card.js';
import { adminSections } from '../../lib/admin/sections.js';

export function AdminLayout() {
  const services = useServices();
  const grants = useGrants();
  const pathname = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();

  if (services.backend.kind === 'node') {
    return (
      <Card className="mx-auto max-w-2xl" data-testid="admin-cloud-only">
        <CardHeader>
          <CardTitle>Admin is in the cloud portal</CardTitle>
          <CardDescription>Users, groups, sites, device secrets, the audit log and usage are managed in the cloud. This page is served by a studio server ({services.backend.label}).</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const sections: AdminSection[] = adminSections(grants).map((s) => ({ ...s, active: pathname === s.href || pathname.startsWith(`${s.href}/`) }));
  if (sections.length === 0) {
    return (
      <Card className="mx-auto max-w-2xl" data-testid="admin-forbidden">
        <CardHeader>
          <CardTitle>No admin access</CardTitle>
          <CardDescription>The Admin area needs tenant administration (or, for usage, billing) permission.</CardDescription>
        </CardHeader>
        <CardContent />
      </Card>
    );
  }
  return (
    <div className="mx-auto grid max-w-6xl gap-4" data-testid="admin-area">
      <AdminNav
        sections={sections}
        onNavigate={(href) => {
          void navigate({ to: href });
        }}
      />
      <Outlet />
    </div>
  );
}
